import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SparkleEffect } from '../components/SparkleEffect';
import { GlitterHeart } from '../components/GlitterHeart';
import { SplitFlapQuote } from '../components/SplitFlapQuote';
import { ArrowRightIcon, HeartIcon, MessageCircleIcon, PhoneIcon } from 'lucide-react';
export function FreeResourcesPage() {
  return <div className="min-h-screen bg-white pt-24 pb-24" data-id="element-2331">
      {/* Hero */}
      <section className="relative py-20 text-center overflow-hidden bg-gradient-to-b from-blush-50 to-white" data-id="element-2332">
        <SparkleEffect count={80} data-id="element-2333" />
        <div className="max-w-3xl mx-auto px-4 relative z-10" data-id="element-2334">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-5xl md:text-6xl font-bold text-plum-700 mb-6" data-id="element-2335">
            Trippin' Through <span className="text-cta-500" data-id="element-2336">Guidance</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-plum-500 font-medium" data-id="element-2337">
            Tools, support, and a gentle reminder that you don't have to figure
            it all out alone.
          </motion.p>
        </div>
      </section>

      {/* SPARKLY QUOTE MARQUEE */}
      <SplitFlapQuote data-id="element-2338" />

      {/* REDESIGNED CRISIS SECTION */}
      <section className="py-16 bg-gradient-to-br from-lavender-50 via-blush-50 to-pink-50 relative overflow-hidden" data-id="element-2339">
        <SparkleEffect count={40} data-id="element-2340" />

        {/* Decorative blurred circles for soft glow */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-2341" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-100/40 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none" data-id="element-2342" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2343">
          <div className="text-center mb-12" data-id="element-2344">
            <GlitterHeart size={40} variant="light" className="mx-auto mb-4" data-id="element-2345" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-plum-700 mb-3" data-id="element-2346">
              If You're Not Okay Right Now
            </h2>
            <p className="text-plum-600 text-lg font-medium max-w-2xl mx-auto" data-id="element-2347">
              You're not alone. You don't have to go through this by yourself.
            </p>
          </div>

          {/* IMMEDIATE CRISIS HELP */}
          <div className="mb-12" data-id="element-2348">
            <h3 className="font-heading text-xl font-bold text-plum-700 mb-6 flex items-center gap-2" data-id="element-2349">
              <span className="text-2xl" data-id="element-2350">🚨</span> Immediate Crisis Help
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2351">
              {/* Card 1: Lifeline */}
              <div className="bg-white rounded-3xl p-6 shadow-md border border-blush-100 flex flex-col h-full" data-id="element-2352">
                <div className="flex items-center gap-3 mb-4" data-id="element-2353">
                  <div className="w-10 h-10 rounded-full bg-sky-50 flex items-center justify-center shrink-0" data-id="element-2354">
                    <PhoneIcon className="w-5 h-5 text-sky-500" data-id="element-2355" />
                  </div>
                  <h4 className="font-bold text-plum-800 text-lg" data-id="element-2356">
                    Suicide & Crisis Lifeline
                  </h4>
                </div>
                <div className="mb-4 flex-grow" data-id="element-2357">
                  <p className="text-plum-600 font-medium mb-1" data-id="element-2358">Call or text</p>
                  <p className="text-4xl font-black text-plum-800 mb-3 tracking-tight" data-id="element-2359">
                    988
                  </p>
                  <p className="text-plum-500 text-sm font-medium flex items-center gap-1.5 mb-1" data-id="element-2360">
                    <span className="text-cta-400" data-id="element-2361">Chat:</span> 988lifeline.org
                  </p>
                  <p className="text-plum-500 text-sm font-medium flex items-center gap-1.5" data-id="element-2362">
                    <span data-id="element-2363">🕒</span> Available 24/7, free & confidential
                  </p>
                </div>
                <a href="https://988lifeline.org/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-sky-50 text-sky-600 hover:bg-sky-100 font-bold transition-colors mt-auto" data-id="element-2364">
                  Get Help Now <ArrowRightIcon className="w-4 h-4" data-id="element-2365" />
                </a>
              </div>

              {/* Card 2: Text Line */}
              <div className="bg-white rounded-3xl p-6 shadow-md border border-blush-100 flex flex-col h-full" data-id="element-2366">
                <div className="flex items-center gap-3 mb-4" data-id="element-2367">
                  <div className="w-10 h-10 rounded-full bg-cta-50 flex items-center justify-center shrink-0" data-id="element-2368">
                    <MessageCircleIcon className="w-5 h-5 text-cta-500" data-id="element-2369" />
                  </div>
                  <h4 className="font-bold text-plum-800 text-lg" data-id="element-2370">
                    Crisis Text Line
                  </h4>
                </div>
                <div className="mb-4 flex-grow" data-id="element-2371">
                  <p className="text-plum-600 font-medium mb-1" data-id="element-2372">
                    Text <strong className="text-plum-800" data-id="element-2373">HOME</strong> to
                  </p>
                  <p className="text-4xl font-black text-plum-800 mb-3 tracking-tight" data-id="element-2374">
                    741741
                  </p>
                  <p className="text-plum-500 text-sm font-medium mb-1" data-id="element-2375">
                    Connects you with a trained crisis counselor
                  </p>
                  <p className="text-plum-500 text-sm font-medium flex items-center gap-1.5" data-id="element-2376">
                    <span data-id="element-2377">🕒</span> Available 24/7
                  </p>
                </div>
                <a href="https://www.crisistextline.org/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-cta-50 text-cta-600 hover:bg-cta-100 font-bold transition-colors mt-auto" data-id="element-2378">
                  Talk to Someone <ArrowRightIcon className="w-4 h-4" data-id="element-2379" />
                </a>
              </div>

              {/* Card 3: Emergency */}
              <div className="bg-white rounded-3xl p-6 shadow-md border border-blush-100 flex flex-col h-full" data-id="element-2380">
                <div className="flex items-center gap-3 mb-4" data-id="element-2381">
                  <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0" data-id="element-2382">
                    <span className="text-xl" data-id="element-2383">🚓</span>
                  </div>
                  <h4 className="font-bold text-plum-800 text-lg" data-id="element-2384">Emergency</h4>
                </div>
                <div className="mb-4 flex-grow" data-id="element-2385">
                  <p className="text-plum-600 font-medium mb-1" data-id="element-2386">Call</p>
                  <p className="text-4xl font-black text-red-500 mb-3 tracking-tight" data-id="element-2387">
                    911
                  </p>
                  <p className="text-plum-500 text-sm font-medium" data-id="element-2388">
                    If someone is in immediate danger
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* SPECIALIZED SUPPORT */}
          <div className="mb-8" data-id="element-2389">
            <h3 className="font-heading text-xl font-bold text-plum-700 mb-6 flex items-center gap-2" data-id="element-2390">
              <span className="text-2xl" data-id="element-2391">💖</span> Specialized Support
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2392">
              {/* Trevor Project */}
              <div className="bg-white rounded-3xl p-6 shadow-sm border border-blush-100" data-id="element-2393">
                <div className="flex items-center gap-3 mb-3" data-id="element-2394">
                  <span className="text-2xl" data-id="element-2395">🌈</span>
                  <h4 className="font-bold text-plum-800" data-id="element-2396">LGBTQ+ Support</h4>
                </div>
                <p className="text-plum-500 text-xs font-bold uppercase tracking-wider mb-3" data-id="element-2397">
                  The Trevor Project
                </p>
                <div className="space-y-2 mb-4" data-id="element-2398">
                  <p className="text-plum-700 font-bold text-lg" data-id="element-2399">
                    1-866-488-7386
                  </p>
                  <p className="text-plum-600 text-sm font-medium" data-id="element-2400">
                    Text <strong className="text-plum-800" data-id="element-2401">START</strong> to{' '}
                    <strong className="text-plum-800" data-id="element-2402">678678</strong>
                  </p>
                  <p className="text-plum-500 text-sm font-medium flex items-center gap-1.5" data-id="element-2403">
                    <span data-id="element-2404">🕒</span> 24/7 support for LGBTQ+ youth
                  </p>
                </div>
                <a href="https://www.thetrevorproject.org/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-sm gap-1 text-cta-500 hover:text-cta-600 font-bold transition-colors" data-id="element-2405">
                  Get Support <ArrowRightIcon className="w-3 h-3" data-id="element-2406" />
                </a>
              </div>

              {/* SAMHSA */}
              <div className="bg-white rounded-3xl p-6 shadow-sm border border-blush-100" data-id="element-2407">
                <div className="flex items-center gap-3 mb-3" data-id="element-2408">
                  <span className="text-2xl" data-id="element-2409">🧠</span>
                  <h4 className="font-bold text-plum-800" data-id="element-2410">
                    Mental Health & Substance Use
                  </h4>
                </div>
                <p className="text-plum-500 text-xs font-bold uppercase tracking-wider mb-3" data-id="element-2411">
                  SAMHSA National Helpline
                </p>
                <div className="space-y-2" data-id="element-2412">
                  <p className="text-plum-700 font-bold text-lg" data-id="element-2413">
                    1-800-662-4357
                  </p>
                  <p className="text-plum-500 text-sm font-medium flex items-center gap-1.5" data-id="element-2414">
                    <span data-id="element-2415">🕒</span> 24/7, free, confidential
                  </p>
                </div>
              </div>

              {/* Postpartum */}
              <div className="bg-white rounded-3xl p-6 shadow-sm border border-blush-100" data-id="element-2416">
                <div className="flex items-center gap-3 mb-3" data-id="element-2417">
                  <span className="text-2xl" data-id="element-2418">👩‍👧</span>
                  <h4 className="font-bold text-plum-800" data-id="element-2419">
                    Postpartum Support
                  </h4>
                </div>
                <p className="text-plum-500 text-xs font-bold uppercase tracking-wider mb-3" data-id="element-2420">
                  For Moms (EN/ES)
                </p>
                <div className="space-y-2 mb-4" data-id="element-2421">
                  <p className="text-plum-600 text-sm font-medium" data-id="element-2422">
                    Call or text:
                  </p>
                  <p className="text-plum-700 font-bold text-lg" data-id="element-2423">
                    1-800-944-4773
                  </p>
                  <p className="text-plum-500 text-sm font-medium flex items-center gap-1.5" data-id="element-2424">
                    <span data-id="element-2425">🕒</span> Not 24/7, but very responsive
                  </p>
                </div>
                <a href="https://www.postpartum.net/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-sm gap-1 text-cta-500 hover:text-cta-600 font-bold transition-colors" data-id="element-2426">
                  Visit Website <ArrowRightIcon className="w-3 h-3" data-id="element-2427" />
                </a>
              </div>

              {/* Domestic Violence */}
              <div className="bg-white rounded-3xl p-6 shadow-sm border border-blush-100" data-id="element-2428">
                <div className="flex items-center gap-3 mb-3" data-id="element-2429">
                  <span className="text-2xl" data-id="element-2430">💔</span>
                  <h4 className="font-bold text-plum-800" data-id="element-2431">Domestic Violence</h4>
                </div>
                <p className="text-plum-500 text-xs font-bold uppercase tracking-wider mb-3" data-id="element-2432">
                  National Hotline
                </p>
                <div className="space-y-2 mb-4" data-id="element-2433">
                  <p className="text-plum-700 font-bold text-lg" data-id="element-2434">
                    1-800-799-7233
                  </p>
                  <p className="text-plum-600 text-sm font-medium" data-id="element-2435">
                    Text <strong className="text-plum-800" data-id="element-2436">START</strong> to{' '}
                    <strong className="text-plum-800" data-id="element-2437">88788</strong>
                  </p>
                  <p className="text-plum-500 text-sm font-medium flex items-center gap-1.5" data-id="element-2438">
                    <span data-id="element-2439">🕒</span> 24/7
                  </p>
                </div>
                <a href="https://www.thehotline.org/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-sm gap-1 text-cta-500 hover:text-cta-600 font-bold transition-colors" data-id="element-2440">
                  Get Support <ArrowRightIcon className="w-3 h-3" data-id="element-2441" />
                </a>
              </div>

              {/* Child Abuse */}
              <div className="bg-white rounded-3xl p-6 shadow-sm border border-blush-100" data-id="element-2442">
                <div className="flex items-center gap-3 mb-3" data-id="element-2443">
                  <span className="text-2xl" data-id="element-2444">👶</span>
                  <h4 className="font-bold text-plum-800" data-id="element-2445">
                    Child Abuse Hotline
                  </h4>
                </div>
                <p className="text-plum-500 text-xs font-bold uppercase tracking-wider mb-3" data-id="element-2446">
                  Childhelp
                </p>
                <div className="space-y-2" data-id="element-2447">
                  <p className="text-plum-600 text-sm font-medium" data-id="element-2448">
                    Call or text:
                  </p>
                  <p className="text-plum-700 font-bold text-lg" data-id="element-2449">
                    1-800-422-4453
                  </p>
                  <p className="text-plum-500 text-sm font-medium flex items-center gap-1.5" data-id="element-2450">
                    <span data-id="element-2451">🕒</span> 24/7
                  </p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-plum-500 font-medium italic mt-12" data-id="element-2452">
            Take a breath. You're going to be okay. 💗
          </p>
        </div>
      </section>

      {/* SECOND MARQUEE - extra positivity */}
      <SplitFlapQuote data-id="element-2453" />

      {/* FREE RESOURCES SECTION */}
      <section className="py-16 bg-white relative overflow-hidden" data-id="element-2454">
        <SparkleEffect count={60} data-id="element-2455" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-2456">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2457">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-plum-700 mb-6" data-id="element-2458">
              Free Resources & Reset
            </h2>
            <p className="text-lg text-plum-600 font-medium leading-relaxed" data-id="element-2459">
              I've been through some really hard things in life—and I know what
              it feels like to feel stuck, overwhelmed, or alone. If that's
              where you are right now, please know this:{' '}
              <strong className="text-plum-800" data-id="element-2460">
                you matter, and you don't have to figure it all out by yourself.
              </strong>
            </p>
          </div>

          {/* Free Coaching Call - thin wide card above the grid */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-10" data-id="element-2461">
            <a href="https://calendly.com/trippinwithmandy/free-consultation-call" target="_blank" rel="noopener noreferrer" className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-gradient-to-r from-cta-50 to-pink-50 rounded-2xl px-8 py-5 border-2 border-cta-200 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all" data-id="element-2462">
              <div className="flex items-center gap-4" data-id="element-2463">
                <span className="text-3xl" data-id="element-2464">💗</span>
                <div data-id="element-2465">
                  <h3 className="font-heading text-lg font-bold text-plum-800" data-id="element-2466">
                    Need Someone to Talk To?
                  </h3>
                  <p className="text-plum-500 font-medium text-sm" data-id="element-2467">
                    Book a free coaching call — no strings, no judgment, just
                    support.
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-cta-500 text-white font-bold text-sm hover:bg-cta-600 transition-colors shadow-lg shadow-cta-500/20 shrink-0" data-id="element-2468">
                Book a Free Call
                <ArrowRightIcon className="w-4 h-4" data-id="element-2469" />
              </span>
            </a>
          </motion.div>

          {/* GRID ROW 1 */}
          <div id="free-lists" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8 scroll-mt-32" data-id="element-2470">
            {/* Interactive Cleaning Schedule Card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-gradient-to-br from-cta-50 to-white rounded-3xl p-8 border-2 border-cta-200 shadow-md flex flex-col relative overflow-hidden" data-id="element-2471">
              <div className="absolute top-4 right-4 bg-cta-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg transform rotate-12" data-id="element-2472">
                NEW ✨
              </div>
              <div className="text-3xl mb-4" data-id="element-2473">✨</div>
              <h3 className="font-heading text-2xl font-bold text-plum-800 mb-4" data-id="element-2474">
                Free Interactive Cleaning Schedule
              </h3>
              <p className="text-plum-600 font-medium mb-8 flex-grow" data-id="element-2475">
                The Ultimate Cleaning Schedule — what to clean & when. Check
                things off, track your progress, use it every week for free!
              </p>
              <Link to="/free-resources/cleaning-schedule" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-cta-500 text-white font-bold text-sm hover:bg-cta-600 transition-colors shadow-lg shadow-cta-500/20 w-full" data-id="element-2476">
                Try It Now
                <ArrowRightIcon className="w-4 h-4" data-id="element-2477" />
              </Link>
            </motion.div>

            {/* Interactive Self-Care Schedule Card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-gradient-to-br from-pink-50 to-white rounded-3xl p-8 border-2 border-pink-200 shadow-md flex flex-col relative overflow-hidden" data-id="element-2478">
              <div className="absolute top-4 right-4 bg-pink-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg transform rotate-12" data-id="element-2479">
                NEW 🧖‍♀️
              </div>
              <div className="text-3xl mb-4" data-id="element-2480">🧖‍♀️</div>
              <h3 className="font-heading text-2xl font-bold text-pink-800 mb-4" data-id="element-2481">
                Free Interactive Self-Care Checklist
              </h3>
              <p className="text-pink-600 font-medium mb-8 flex-grow" data-id="element-2482">
                Daily hygiene and wellness ritual. Take care of your body and
                mind with this simple, trackable daily routine!
              </p>
              <Link to="/free-resources/self-care/daily" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-pink-500 text-white font-bold text-sm hover:bg-pink-600 transition-colors shadow-lg shadow-pink-500/20 w-full" data-id="element-2483">
                Try It Now
                <ArrowRightIcon className="w-4 h-4" data-id="element-2484" />
              </Link>
            </motion.div>

            {/* Free Pre-Trip Home Prep Card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.15
          }} className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 border-2 border-emerald-200 shadow-md flex flex-col relative overflow-hidden" data-id="element-2485">
              <div className="absolute top-4 right-4 bg-emerald-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg transform rotate-12" data-id="element-2486">
                NEW 🏡
              </div>
              <div className="text-3xl mb-4" data-id="element-2487">🏡</div>
              <h3 className="font-heading text-2xl font-bold text-emerald-800 mb-4" data-id="element-2488">
                Free Pre-Trip Home Prep Checklist
              </h3>
              <p className="text-emerald-600 font-medium mb-8 flex-grow" data-id="element-2489">
                What to do before you leave so you come back to a peaceful,
                clean home. 10 sections covering everything from kitchen prep to
                security!
              </p>
              <Link to="/free-resources/travel/home-prep" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-emerald-500 text-white font-bold text-sm hover:bg-emerald-600 transition-colors shadow-lg shadow-emerald-500/20 w-full" data-id="element-2490">
                Try It Now
                <ArrowRightIcon className="w-4 h-4" data-id="element-2491" />
              </Link>
            </motion.div>

            {/* FREE WORKOUT CONSULTATION Card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-gradient-to-br from-sky-50 to-lavender-50 rounded-3xl p-8 border-2 border-sky-200 shadow-md flex flex-col relative overflow-hidden" data-id="element-2492">
              <div className="absolute top-4 right-4 bg-sky-500 text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg transform rotate-12" data-id="element-2493">
                FREE 💪
              </div>
              <div className="text-3xl mb-4" data-id="element-2494">💪</div>
              <h3 className="font-heading text-2xl font-bold text-plum-800 mb-4" data-id="element-2495">
                Free Workout Consultation
              </h3>
              <p className="text-plum-600 font-medium mb-3 flex-grow" data-id="element-2496">
                Meet Jake — once the smallest in the room, now lifting over 500
                lbs and training for championship-level competition. His journey
                is proof of what consistency and discipline can do.
              </p>
              <p className="text-plum-500 text-sm font-medium mb-6" data-id="element-2497">
                Free consultations, zero pressure — just real advice from
                someone who's been exactly where you are.
              </p>
              <a href="https://calendly.com/trippinwithmandy/free-consultation-call" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-sky-500 text-white font-bold text-sm hover:bg-sky-600 transition-colors shadow-lg shadow-sky-500/20 w-full" data-id="element-2498">
                Book Free Consultation
                <ArrowRightIcon className="w-4 h-4" data-id="element-2499" />
              </a>
            </motion.div>
          </div>

          {/* PANIC ATTACK GROUNDING - Full width between rows */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-gradient-to-br from-blush-50 to-lavender-50 rounded-3xl p-8 md:p-10 shadow-lg border border-blush-100 my-12" data-id="element-2500">
            <div className="text-center mb-6" data-id="element-2501">
              <div className="text-4xl mb-3" data-id="element-2502">🫧</div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-plum-700 mb-2" data-id="element-2503">
                Struggling with a Panic Attack?
              </h2>
              <p className="text-plum-500 font-semibold" data-id="element-2504">
                If you're having anxiety or a panic attack, try this grounding
                technique:
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto" data-id="element-2505">
              <div className="bg-white/80 rounded-2xl p-6 text-center border border-blush-100 shadow-sm" data-id="element-2506">
                <span className="text-3xl block mb-3" data-id="element-2507">👀</span>
                <h3 className="font-bold text-plum-800 text-lg mb-1" data-id="element-2508">
                  See 5 things
                </h3>
                <p className="text-plum-500 font-medium text-sm" data-id="element-2509">
                  Look around and name 5 things you can see right now.
                </p>
              </div>
              <div className="bg-white/80 rounded-2xl p-6 text-center border border-blush-100 shadow-sm" data-id="element-2510">
                <span className="text-3xl block mb-3" data-id="element-2511">✋</span>
                <h3 className="font-bold text-plum-800 text-lg mb-1" data-id="element-2512">
                  Feel 5 things
                </h3>
                <p className="text-plum-500 font-medium text-sm" data-id="element-2513">
                  Touch 5 different textures — your shirt, the table, your hair.
                </p>
              </div>
              <div className="bg-white/80 rounded-2xl p-6 text-center border border-blush-100 shadow-sm" data-id="element-2514">
                <span className="text-3xl block mb-3" data-id="element-2515">👂</span>
                <h3 className="font-bold text-plum-800 text-lg mb-1" data-id="element-2516">
                  Hear 5 things
                </h3>
                <p className="text-plum-500 font-medium text-sm" data-id="element-2517">
                  Close your eyes and listen for 5 sounds around you.
                </p>
              </div>
            </div>
            <p className="text-cta-500 font-bold text-center mt-6 text-sm italic" data-id="element-2518">
              This helps bring your mind back to the present moment. You're
              going to be okay. 💗
            </p>
          </motion.div>

          {/* GRID ROW 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16" data-id="element-2519">
            {/* Card 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-blush-50 rounded-3xl p-8 border border-blush-100 shadow-sm" data-id="element-2520">
              <div className="text-3xl mb-4" data-id="element-2521">🧘‍♀️</div>
              <h3 className="font-heading text-2xl font-bold text-plum-700 mb-4" data-id="element-2522">
                Things You Can Do Right Now
              </h3>
              <ul className="space-y-3 text-plum-600 font-medium" data-id="element-2523">
                <li className="flex items-start gap-2" data-id="element-2524">
                  <span className="text-cta-500 mt-1" data-id="element-2525">•</span> Take a deep
                  breath (seriously—slow it down)
                </li>
                <li className="flex items-start gap-2" data-id="element-2526">
                  <span className="text-cta-500 mt-1" data-id="element-2527">•</span> Drink some water
                </li>
                <li className="flex items-start gap-2" data-id="element-2528">
                  <span className="text-cta-500 mt-1" data-id="element-2529">•</span> Step outside for
                  a few minutes
                </li>
                <li className="flex items-start gap-2" data-id="element-2530">
                  <span className="text-cta-500 mt-1" data-id="element-2531">•</span> Text someone you
                  trust
                </li>
                <li className="flex items-start gap-2" data-id="element-2532">
                  <span className="text-cta-500 mt-1" data-id="element-2533">•</span> Put on music that
                  grounds you
                </li>
              </ul>
            </motion.div>

            {/* Card 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-lavender-50 rounded-3xl p-8 border border-lavender-100 shadow-sm" data-id="element-2534">
              <div className="text-3xl mb-4" data-id="element-2535">📓</div>
              <h3 className="font-heading text-2xl font-bold text-plum-700 mb-4" data-id="element-2536">
                Quick Reset Questions
              </h3>
              <ul className="space-y-3 text-plum-600 font-medium" data-id="element-2537">
                <li className="flex items-start gap-2" data-id="element-2538">
                  <span className="text-cta-500 mt-1" data-id="element-2539">•</span> What am I feeling
                  right now?
                </li>
                <li className="flex items-start gap-2" data-id="element-2540">
                  <span className="text-cta-500 mt-1" data-id="element-2541">•</span> What do I
                  actually need today?
                </li>
                <li className="flex items-start gap-2" data-id="element-2542">
                  <span className="text-cta-500 mt-1" data-id="element-2543">•</span> What's one small
                  thing I can do?
                </li>
              </ul>
            </motion.div>

            {/* Card 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} className="bg-sky-50 rounded-3xl p-8 border border-sky-100 shadow-sm" data-id="element-2544">
              <div className="text-3xl mb-4" data-id="element-2545">🎧</div>
              <h3 className="font-heading text-2xl font-bold text-plum-700 mb-4" data-id="element-2546">
                My Go-To Distractions
              </h3>
              <ul className="space-y-3 text-plum-600 font-medium" data-id="element-2547">
                <li className="flex items-start gap-2" data-id="element-2548">
                  <span className="text-cta-500 mt-1" data-id="element-2549">•</span> A comfort show
                  (Gilmore Girls, anyone?)
                </li>
                <li className="flex items-start gap-2" data-id="element-2550">
                  <span className="text-cta-500 mt-1" data-id="element-2551">•</span> A podcast that
                  feels like talking to a friend
                </li>
                <li className="flex items-start gap-2" data-id="element-2552">
                  <span className="text-cta-500 mt-1" data-id="element-2553">•</span> Scrolling through
                  travel TikToks
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}